import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";

import { MemberInfo } from "../../../types/member";

import { getMainNavRoutes, RouteInfo } from "../../../libs/routes";
import { getLocalValue } from "../../../libs/localStorage";
import { reissueToken } from "../../../libs/request";

import { useUserContext } from "../../../contexts/userContext";

import usePushKey from "../../../hooks/usePush";
import userMemberInfo from "../../../hooks/useMemberInfo";
import { useDeviceType } from "../../../hooks/useDeviceType/Index";
import useAppVersion from "../../../hooks/useAppVersion";
import useShowQualtrics from "../../../hooks/useShowQualtrics";
import useSimplePopup from "../../../hooks/useSimplePopup";

import CheckWorkSpacePopup from "../../Popup/CheckWorkSpace";
import { getQualtricsList } from "../../../libs/qualtrics";
import { getUserLogoInfo } from "../../../services/authServices";
import { HttpStatusCode } from "axios";
import { FullScreenLoadingBar } from "../../LoadingBar";

type Props = {};

// eslint-disable-next-line no-empty-pattern
export default function AuthenticatedLayout(props: Props) {
  const mainNav: RouteInfo[] = getMainNavRoutes();
  const navigate = useNavigate();
  const location = useLocation();

  const [popup, setPopup] = React.useState<string>("");

  const deviceType = useDeviceType();
  const [user, { updateField }] = useUserContext();
  const [appVersionResult, versionCall] = useAppVersion();
  const [, pushCall] = usePushKey();
  const { loadingUserData } = userMemberInfo({
    updateField,
    pushCall,
    deviceType,
    appVersion: appVersionResult.result,
  });
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [showQualtrics] = useShowQualtrics({ open });

  const isNoNav = [
    "/service/hot-cold-vote",
    "/manager/manager-hot-cold-vote",
  ].some((path) => location.pathname.startsWith(path));

  const isActive = (menu: RouteInfo) => {
    return (
      location.pathname.startsWith(`/${menu.path}`) ||
      (location.pathname === "/" && menu.path === "view")
    );
  };

  const loginCheck = async () => {
    const isLogin = getLocalValue("jwtToken");
    if (isLogin) return true;
    return false;

    // const jwtToken = JSON.parse(isLogin as string);
    // try {
    //   console.log("authenticatedLayout jwt reIssue");
    //   await reissueToken(jwtToken);
    // } catch (e: any) {
    //   console.error("AuthenticatedLayout reissueToken error", e);
    //   return false;
    // }
    // return true;
  };

  const checkSession = async () => {
    const isLogin = await loginCheck();
    if (isLogin === false) {
      const isAutoLogin = getLocalValue("AUTO");
      if (!isAutoLogin) {
        navigate(`/auth/login?redirect=${location.pathname}${location.hash}`, {
          replace: true,
        });
      } else {
        try {
          await reissueToken({ refreshToken: isAutoLogin });
        } catch (e: any) {
          console.log("reissueToken error", e);
        }
      }
    } else {
      console.log("기존 로그인 정보로 로그인 성공");
    }

    loadingUserData();
  };

  useEffect(() => {
    if (
      user.checkEvacuationSituation &&
      !["/manager/evacuation-manager"].some((path) =>
        location.pathname.startsWith(path)
      )
    ) {
      navigate(`/evacuation/situation`, {
        replace: true,
      });
    }
  }, [user.checkEvacuationSituation]);

  const getLogoInfo = async () => {
    try {
      const response = await getUserLogoInfo();
      if (response.status === HttpStatusCode.Ok) {
        updateField({ logoUrl: response.data.logoUrl });
      } else {
        console.log("response Error - status : ", response.status);
      }
    } catch (err: any) {
      console.log("getLogoInfo Error", err);
    }
  };

  useEffect(() => {
    getLogoInfo();
  }, [user.buildingId]);

  useEffect(() => {
    document.getElementById("authenticated_outlet")?.scrollIntoView();
  }, [location.pathname]);

  useEffect(() => {
    if (user.isLoading) return;
    if (!location.pathname) return;

    const qualtricsList = getQualtricsList()[location.pathname];
    if (!qualtricsList || qualtricsList.length === 0) return;

    const { isLoading, floor, building, ...memberInfo } = user;
    showQualtrics(qualtricsList.join(","), memberInfo as MemberInfo);
  }, [user.isLoading, location.pathname]);

  useEffect(() => {
    if (user.isLoading === false && !user.buildingId) {
      setPopup("checkWorkSpace");
    }
  }, [user.isLoading]);

  useEffect(() => {
    if (user.isLoading === false || appVersionResult.isLoading) return;

    if (appVersionResult.result && deviceType) {
      checkSession();
    } else if (
      // 23.12.11 이건우 파트너 요청사항
      // TODO: 샌디앱을 웹으로 이용하는 사업장이 존재하여 우선 임시로 웹 오픈 후 대응 방안이 마련되면 else if 구문 제거 필요 (app.tsx에도 수정 필요)
      "result" in appVersionResult &&
      appVersionResult.result === undefined
    ) {
      checkSession();
    }
  }, [
    user.isLoading,
    appVersionResult.isLoading,
    appVersionResult.result,
    deviceType,
  ]);

  useEffect(() => {
    versionCall();
  }, []);

  const loading : boolean = useMemo( () => {
    return user.isLoading !== false || appVersionResult.isLoading !== false
  }, [user.isLoading, appVersionResult.isLoading,])

  if (loading) {
    return <FullScreenLoadingBar isLoading transparent/>
  }

  return (
    <div className="min-h-screen relative">
      <div
        className={clsx([
          "bg-white min-h-screen w-full flex flex-col overflow-scroll main-div",
          {
            "pb-[68px]":
              !["/service/custom"].includes(location.pathname) && !isNoNav,
            "pb-5": isNoNav,
          },
        ])}
        id="authenticated_outlet"
      >
        <Outlet />
      </div>
      {user.isLoading === false && !user.buildingId && (
        <CheckWorkSpacePopup popup={popup} onClose={() => setPopup("")} />
      )}

      {![
        "/report",
        "/talk/report",
        "/myinfo/change/email",
        "/myinfo/change/building",
        "/myinfo/change/password",
        "/myinfo/config/simple/login",
        "/myinfo/config/biometric/authentication",
        "/myinfo/config/push",
        "/myinfo/config/app",
        "/myinfo/cert/company",
        "/myinfo/cert/identity",
        "/service/",
        "/manager/",
        "/history/",
        "/alarm",
      ].some((item) => location.pathname.startsWith(item)) ? (
        <footer className="fixed left-0 right-0 bottom-0 z-20 ">
          <div className="bg-bottom-shadow h-[5px] w-full"></div>
          <div className="h-[68px] bg-white grid grid-cols-5 px-[22px] pt-[9px] pb-[13px] gap-6 ">
            {mainNav.slice(0, 5).map((menu, index) => (
              <div
                key={index}
                className={`flex flex-col items-center h-full text-[10px] tracking-[-0.4px] gap-0.5 whitespace-nowrap ${
                  isActive(menu)
                    ? "text-brand-gray-140 font-semibold"
                    : "text-brand-primary-gray-100 text-opacity-40 font-medium"
                } justify-center`}
                onClick={() => navigate(`/${menu.path}`)}
              >
                <div
                  className={`rounded-full flex justify-center h-[32px] w-[32px] items-center`}
                >
                  {isActive(menu) ? menu.activeIcon : menu.icon}
                </div>
                {menu.title}
              </div>
            ))}
          </div>
        </footer>
      ) : null}
      {simplePopupComponent}
    </div>
  );
}
